import { IconCheckmark1Small } from "central-icons/IconCheckmark1Small";
import { IconChevronGrabberVertical } from "central-icons/IconChevronGrabberVertical";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type ComboboxOption = {
  value: string;
  label: string;
  searchLabel?: string;
  [key: string]: unknown;
};

type BaseComboboxProps = {
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  disabled?: boolean;
  showSearch?: boolean;
  renderOption?: (option: ComboboxOption) => React.ReactNode;
  id?: string;
};

type SingleComboboxProps = BaseComboboxProps & {
  multiple?: false;
  value?: string;
  onValueChange: (value: string) => void;
  renderTrigger?: (
    selectedOption: ComboboxOption | undefined
  ) => React.ReactNode;
};

type MultiComboboxProps = BaseComboboxProps & {
  multiple: true;
  value?: string[];
  onValueChange: (value: string[]) => void;
  renderTrigger?: (selectedOptions: ComboboxOption[]) => React.ReactNode;
};

type ComboboxProps = SingleComboboxProps | MultiComboboxProps;

export function Combobox(props: ComboboxProps) {
  const {
    options,
    placeholder = "Select option...",
    searchPlaceholder = "Search...",
    emptyText = "No results found.",
    className,
    disabled = false,
    showSearch = true,
    renderOption,
    id,
  } = props;

  const [open, setOpen] = useState(false);

  const isMultiple = props.multiple === true;

  const getSelectedValues = (): string[] => {
    if (isMultiple) {
      return props.value ?? [];
    }
    return props.value ? [props.value as string] : [];
  };
  const selectedValues = getSelectedValues();

  const selectedOptions: ComboboxOption[] = options.filter((option) =>
    selectedValues.includes(option.value)
  );

  const isSelected = (optionValue: string) =>
    selectedValues.includes(optionValue);

  const handleSelect = (optionValue: string) => {
    if (isMultiple) {
      const newValues = isSelected(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];
      (props as MultiComboboxProps).onValueChange(newValues);
    } else {
      (props as SingleComboboxProps).onValueChange(optionValue);
      setOpen(false);
    }
  };

  const handleRemove = (
    optionValue: string,
    e: React.MouseEvent | React.KeyboardEvent
  ) => {
    e.stopPropagation();

    if ("key" in e && e.key !== "Enter" && e.key !== " ") {
      return;
    }

    if (isMultiple) {
      (props as MultiComboboxProps).onValueChange(
        selectedValues.filter((v) => v !== optionValue)
      );
    }
  };

  const renderDefaultTrigger = () => {
    if (isMultiple) {
      return (
        <div className="flex flex-1 flex-wrap gap-1">
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <Badge className="gap-1" key={option.value} variant="default">
                {option.label}
                <button
                  aria-label={`Remove ${option.label}`}
                  className="rounded-full outline-none hover:bg-gray-200 focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onClick={(e) => handleRemove(option.value, e)}
                  onKeyDown={(e) => handleRemove(option.value, e)}
                  type="button"
                >
                  <IconCrossSmall className="size-3" />
                </button>
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </div>
      );
    }

    const selectedOption = selectedOptions.at(0);
    return (
      <span className={cn(!selectedOption && "text-muted-foreground")}>
        {selectedOption ? selectedOption.label : placeholder}
      </span>
    );
  };

  const triggerContent = () => {
    if (props.renderTrigger) {
      if (isMultiple) {
        return (props as MultiComboboxProps).renderTrigger?.(selectedOptions);
      }
      return (props as SingleComboboxProps).renderTrigger?.(
        selectedOptions.at(0)
      );
    }
    return renderDefaultTrigger();
  };

  const dropdownContent = (
    <PopoverContent
      align="start"
      className="w-[var(--radix-popover-trigger-width)] p-0"
    >
      <Command>
        {showSearch && <CommandInput placeholder={searchPlaceholder} />}
        <CommandList>
          <CommandEmpty>{emptyText}</CommandEmpty>
          <CommandGroup>
            {options.map((option) => (
              <CommandItem
                key={option.value}
                keywords={option.searchLabel ? [option.searchLabel] : undefined}
                onSelect={() => handleSelect(option.value)}
                value={option.value}
              >
                {renderOption ? (
                  renderOption(option)
                ) : (
                  <>
                    <IconCheckmark1Small
                      className={cn(
                        "mr-2 size-4",
                        isSelected(option.value) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );

  if (isMultiple) {
    return (
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild disabled={disabled}>
          <div
            aria-expanded={open}
            aria-haspopup="listbox"
            className={cn(
              "flex h-auto min-h-10 w-full cursor-pointer items-center justify-between rounded-select border border-input bg-gray-50 px-4 py-2 font-medium text-sm dark:bg-gray-900",
              "hover:bg-gray-100 dark:hover:bg-gray-800",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              disabled && "pointer-events-none opacity-50",
              className
            )}
            id={id}
            role="combobox"
            tabIndex={disabled ? -1 : 0}
          >
            {triggerContent()}
            <IconChevronGrabberVertical className="ml-2 size-4 shrink-0 opacity-50" />
          </div>
        </PopoverTrigger>
        {dropdownContent}
      </Popover>
    );
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className={cn(
            "h-10 w-full justify-between rounded-select bg-gray-50 px-4 py-3 font-medium text-sm dark:bg-gray-900",
            className
          )}
          disabled={disabled}
          id={id}
          role="combobox"
          type="button"
          variant="ghost"
        >
          {triggerContent()}
          <IconChevronGrabberVertical className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {dropdownContent}
    </Popover>
  );
}
